import React, { useEffect, useState } from 'react';
import { getSeedReports, getAllAnalyses } from '../utils/api';
import { Crosshair, Zap, AlertCircle, TrendingDown } from 'lucide-react';
import './AnalysisPage.css';

export default function OppositionScoutingPage() {
  const [reports, setReports] = useState([]);

  useEffect(() => {
    Promise.all([getSeedReports(), getAllAnalyses().catch(() => ({ data: [] }))])
      .then(([seed, mine]) => {
        const userReports = (mine.data || []).filter(r => r.status === 'complete' && r.report);
        setReports([...userReports, ...(seed.data || [])]);
      });
  }, []);

  return (
    <div className="analysis-page fade-in">
      <div className="analysis-page__header">
        <h1>Opposition Scouting</h1>
        <p>AI-generated intelligence on upcoming and past opponents</p>
      </div>

      <div className="opposition-grid stagger">
        {reports.map((r, i) => {
          const opp = r.report?.oppositionTendencies;
          const weaknesses = r.report?.tacticalWeaknesses?.filter(w => w.severity === 'high') || [];
          return (
            <div key={i} className="card opp-card">
              <div className="opp-header">
                <div>
                  <div className="opp-team">{r.awayTeam}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>{r.competition} · {r.matchDate}</div>
                </div>
                <span className="badge badge-red">Scouted</span>
              </div>

              {r.report?.formations?.away && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Formation</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 14, color: 'var(--text-primary)', fontWeight: 600 }}>
                    {r.report.formations.away.name}
                  </span>
                </div>
              )}

              {opp?.description && (
                <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 14 }}>
                  {opp.description.slice(0, 160)}...
                </p>
              )}

              {opp?.exploitableAreas?.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <Zap size={11} style={{ color: '#f59e0b' }} /> Exploitable Areas
                  </div>
                  {opp.exploitableAreas.slice(0, 3).map((a, j) => (
                    <div key={j} className="exploit-tag" style={{ display: 'flex', marginBottom: 4 }}>{a}</div>
                  ))}
                </div>
              )}

              {weaknesses.length > 0 && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <TrendingDown size={11} style={{ color: '#ef4444' }} /> Key Weaknesses
                  </div>
                  {weaknesses.slice(0, 2).map((w, j) => (
                    <div key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: 7, fontSize: 12.5, color: 'var(--text-secondary)', padding: '5px 0', borderBottom: '1px solid var(--border-subtle)' }}>
                      <AlertCircle size={11} style={{ color: '#ef4444', flexShrink: 0, marginTop: 2 }} />
                      {w.title}
                    </div>
                  ))}
                </div>
              )}

              {r.report?.pressingAnalysis?.away && (
                <div style={{ marginTop: 14, padding: '10px 12px', background: 'var(--bg-elevated)', borderRadius: 8 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Press Intensity</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: r.report.pressingAnalysis.away.intensity >= 7 ? '#ef4444' : r.report.pressingAnalysis.away.intensity >= 5 ? '#f59e0b' : '#10b981' }}>
                      {r.report.pressingAnalysis.away.intensity}/10
                    </div>
                    <div className="progress-bar" style={{ flex: 1 }}>
                      <div className="progress-fill" style={{ width: `${r.report.pressingAnalysis.away.intensity * 10}%` }} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {reports.length === 0 && (
          <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
            <Crosshair size={36} style={{ margin: '0 auto 12px', display: 'block', opacity: 0.4 }} />
            <p>No opposition data yet. Upload match videos to begin scouting.</p>
          </div>
        )}
      </div>
    </div>
  );
}
